// Simple Cache Utility
// Basic caching without complex dependencies

interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

class SimpleCache {
  private cache = new Map<string, CacheItem<any>>();
  private maxSize = 50; // Keep cache small to prevent memory issues

  // Set cache item with TTL (time to live) in milliseconds
  public set<T>(key: string, data: T, ttl: number = 300000): void { // 5 minutes default
    const now = Date.now();

    // Clean up if cache is getting too big
    if (this.cache.size >= this.maxSize) {
      this.cleanup();
    }

    this.cache.set(key, {
      data,
      timestamp: now,
      expiresAt: now + ttl
    });
  }

  // Get cache item
  public get<T>(key: string): T | null {
    const item = this.cache.get(key);

    if (!item) {
      return null;
    }

    // Check if expired
    if (Date.now() > item.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return item.data as T;
  }

  // Check if key exists and is valid
  public has(key: string): boolean {
    const item = this.cache.get(key);
    if (!item) return false;

    if (Date.now() > item.expiresAt) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  // Clear specific key
  public delete(key: string): void {
    this.cache.delete(key);
  }

  // Clear all cache
  public clear(): void {
    this.cache.clear();
  }

  // Clean up expired items
  private cleanup(): void {
    const now = Date.now();
    const toDelete: string[] = [];

    for (const [key, item] of this.cache.entries()) {
      if (now > item.expiresAt) {
        toDelete.push(key);
      }
    }

    toDelete.forEach(key => this.cache.delete(key));

    // If still too big, remove oldest items
    if (this.cache.size >= this.maxSize) {
      const entries = Array.from(this.cache.entries());
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);

      const toRemove = entries.slice(0, Math.floor(this.maxSize / 2));
      toRemove.forEach(([key]) => this.cache.delete(key));
    }
  }

  // Get cache stats
  public getStats(): { size: number; maxSize: number } {
    return {
      size: this.cache.size,
      maxSize: this.maxSize
    };
  }
}

// Create singleton instance
export const simpleCache = new SimpleCache();

// React hook for simple caching
export const useSimpleCache = () => {
  const setCache = <T>(key: string, data: T, ttl?: number) => {
    simpleCache.set(key, data, ttl);
  };

  const getCache = <T>(key: string): T | null => {
    return simpleCache.get<T>(key);
  };

  const hasCache = (key: string): boolean => {
    return simpleCache.has(key);
  };

  const deleteCache = (key: string) => {
    simpleCache.delete(key);
  };

  const clearCache = () => {
    simpleCache.clear();
  };

  return {
    setCache,
    getCache,
    hasCache,
    deleteCache,
    clearCache
  };
};

export default simpleCache;